import { useState } from 'react'
import { ChevronDown, Info } from 'lucide-react'
import type { TypeBQuestionnaireExtractionEvidenceEntry } from '@/shared/hooks/api/machineryDirectiveCompliance'
import { cn } from '@/shared/lib/utils'
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

type TranslateFn = (key: string, options?: Record<string, unknown>) => string

type TypeBQuestionnaireExtractionEvidenceBlockProps = {
  evidence: TypeBQuestionnaireExtractionEvidenceEntry
  translate: TranslateFn
}

export function TypeBQuestionnaireExtractionEvidenceBlock({
  evidence,
  translate,
}: TypeBQuestionnaireExtractionEvidenceBlockProps) {
  const [isExcerptOpen, setIsExcerptOpen] = useState(false)
  const hasSourceFilename =
    evidence.sourceFilename != null && evidence.sourceFilename.trim().length > 0
  const hasExcerpt =
    evidence.excerpt != null && evidence.excerpt.trim().length > 0

  return (
    <div className='rounded-md border border-border bg-muted/30 px-3 py-2 text-sm'>
      <p className='text-xs font-medium text-muted-foreground'>
        {translate('onboarding.typeBQuestionnaire.extractionEvidence.fromLexLabel')}
      </p>
      <p className='mt-1 text-foreground'>{evidence.rationale}</p>
      {hasSourceFilename ? (
        <p className='mt-2 text-xs text-muted-foreground'>
          {translate('onboarding.typeBQuestionnaire.extractionEvidence.sourceLabel', {
            filename: evidence.sourceFilename,
          })}
        </p>
      ) : null}
      {hasExcerpt ? (
        <Collapsible open={isExcerptOpen} onOpenChange={setIsExcerptOpen}>
          <CollapsibleTrigger
            render={
              <button
                type='button'
                className='mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary underline-offset-4 hover:underline'
              />
            }
          >
              {translate(
                isExcerptOpen
                  ? 'onboarding.typeBQuestionnaire.extractionEvidence.hideExcerpt'
                  : 'onboarding.typeBQuestionnaire.extractionEvidence.showExcerpt'
              )}
              <ChevronDown
                className={cn(
                  'size-3 transition-transform duration-200',
                  isExcerptOpen && 'rotate-180'
                )}
                aria-hidden
              />
          </CollapsibleTrigger>
          <CollapsibleContent>
            <pre className='mt-2 max-h-40 overflow-y-auto whitespace-pre-wrap rounded-md border border-input bg-background/80 p-2 text-xs text-foreground'>
              {evidence.excerpt}
            </pre>
          </CollapsibleContent>
        </Collapsible>
      ) : null}
    </div>
  )
}

type TypeBQuestionnaireInsufficientEvidenceHintProps = {
  translate: TranslateFn
}

export function TypeBQuestionnaireInsufficientEvidenceHint({
  translate,
}: TypeBQuestionnaireInsufficientEvidenceHintProps) {
  return (
    <div className='flex items-start gap-2 rounded-md border border-dashed border-border bg-muted/20 px-3 py-2 text-sm text-muted-foreground'>
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type='button'
            className='mt-0.5 shrink-0 text-muted-foreground hover:text-foreground'
            aria-label={translate(
              'onboarding.typeBQuestionnaire.extractionEvidence.insufficientTooltip'
            )}
          >
            <Info className='size-4' aria-hidden />
          </button>
        </TooltipTrigger>
        <TooltipContent side='top' className='max-w-sm text-left'>
          {translate('onboarding.typeBQuestionnaire.extractionEvidence.insufficientTooltip')}
        </TooltipContent>
      </Tooltip>
      <p>{translate('onboarding.typeBQuestionnaire.extractionEvidence.insufficientHint')}</p>
    </div>
  )
}
